import { API_ENDPOINTS } from '../../constants/endpoints';
import { get, post } from '../../utils/api.utils';

interface ApiResponse {
  statusCode?: number;
  message?: string;
  [key: string]: any;
}

export class GhlService {
  /**
   * Create or update a contact in GoHighLevel.
   * Returns contactId on success. Swallows errors so signup flow is not blocked.
   */
  public static async createContact(
    leadData: { email: string; firstName?: string; lastName?: string; tags?: string[]; [key: string]: any },
    phone?: string,
    utm_Data?: Record<string, string | null | undefined>
  ): Promise<string | undefined> {
    try {
      const payload: any = {
        email: leadData.email,
        firstName: leadData.firstName ?? leadData.FirstName,
        lastName: leadData.lastName ?? leadData.LastName,
        phone: leadData.phone || phone || '',
        tags: leadData.tags ?? [],
      };
      if (utm_Data) {
        payload.utm_source = utm_Data.utm_source ?? '';
        payload.utm_medium = utm_Data.utm_medium ?? '';
        payload.utm_campaign = utm_Data.utm_campaign ?? '';
        payload.utm_content = utm_Data.utm_content ?? '';
        payload.utm_term = utm_Data.utm_term ?? '';
        payload.ad_id = utm_Data.ad_id ?? '';
        payload.fbc_id = utm_Data.fbc_id ?? '';
        payload.h_ad_id = utm_Data.h_ad_id ?? '';
        payload.utm_id = utm_Data.utm_id ?? '';
        payload.gc_id = utm_Data.gc_id ?? '';
        payload.h_ga_id = utm_Data.h_ga_id ?? '';
        payload.h_keyword_id = utm_Data.h_keyword_id ?? '';
        payload.h_keyword = utm_Data.h_keyword ?? '';
        payload.h_placement = utm_Data.h_placement ?? '';
        payload.g_special_campaign = utm_Data.g_special_campaign ?? '';
      }
      const response = await post(API_ENDPOINTS.GHL.CREATE_CONTACT, payload) as ApiResponse;
      return response?.contactId ?? response?.data?.contactId;
    } catch (error) {
      console.warn('GHL createContact failed (non-fatal):', error);
      return undefined;
    }
  }

  /**
   * Add a note (activity) to a GHL contact.
   */
  public static async addNote(payload: {
    contactId: string;
    activityValue: string;
    activityNote?: string;
  }): Promise<void> {
    try {
      await post(API_ENDPOINTS.GHL.ADD_NOTE, {
        contactId: payload.contactId,
        activityValue: payload.activityValue,
        activityNote: payload.activityNote ?? 'EW-Authenticator',
      });
    } catch (error) {
      console.warn('GHL addNote failed (non-fatal):', error);
    }
  }

  /**
   * Add tags to a GHL contact (e.g. "email verified", "cc trial user", "paid user").
   */
  public static async addTags(contactId: string, tags: string[]): Promise<void> {
    if (!tags?.length) return;
    try {
      await post(API_ENDPOINTS.GHL.ADD_TAGS, { contactId, tags });
    } catch (error) {
      console.warn('GHL addTags failed (non-fatal):', error);
    }
  }

  /**
   * Get GHL contact by email.
   */
  public static async getContactByEmail(email: string): Promise<{ id: string; [key: string]: any } | null> {
    try {
      const response = await get(API_ENDPOINTS.GHL.GET_CONTACT_BY_EMAIL + encodeURIComponent(email));
      const data = (response as any)?.data ?? response;
      if (data?.id) return data;
      if (Array.isArray(data) && data[0]?.id) return data[0];
      return null;
    } catch (error) {
      console.warn('GHL getContactByEmail failed:', error);
      return null;
    }
  }

  /**
   * Update custom fields on a GHL contact.
   */
  public static async updateContactFields(payload: {
    contactId: string;
    fields: Array<{ key: string; value: string }>;
  }): Promise<void> {
    try {
      await post(API_ENDPOINTS.GHL.UPDATE_CONTACT_FIELDS, payload);
    } catch (error) {
      console.warn('GHL updateContactFields failed (non-fatal):', error);
    }
  }

  /**
   * Create contact and add first note in one call (for signup flows).
   */
  public static async createContactAndNote(
    leadData: { email: string; firstName?: string; lastName?: string; activityValue?: string; activityNote?: string; [key: string]: any },
    phone: string,
    utm_Data?: Record<string, string | null | undefined>
  ): Promise<string | undefined> {
    try {
      const payload: any = {
        email: leadData.email,
        firstName: leadData.firstName ?? leadData.FirstName,
        lastName: leadData.lastName ?? leadData.LastName,
        phone: phone || '',
        activityValue: leadData.activityValue ?? 'Signup Attempt',
        activityNote: leadData.activityNote ?? 'EW-Authenticator',
      };
      if (utm_Data) {
        Object.keys(utm_Data).forEach((k) => {
          payload[k] = utm_Data[k] ?? '';
        });
      }
      const response = await post(API_ENDPOINTS.GHL.CREATE_CONTACT_AND_NOTE, payload) as ApiResponse;
      return response?.contactId ?? response?.data?.contactId;
    } catch (error) {
      console.warn('GHL createContactAndNote failed (non-fatal):', error);
      return undefined;
    }
  }
}
